import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Image } from "@/components/ui/image";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { MedalDisplayCase } from "@/features/user/components/medal-display-case";
import type { Medal, User } from "@/features/user/types";
import { queries } from "@/utils/query";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  SiQq,
  SiQqHex,
  SiWechat,
  SiWechatHex,
} from "@icons-pack/react-simple-icons";
import { Link, createFileRoute } from "@tanstack/react-router";
import { CheckIcon, GripHorizontalIcon } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/user/settings/")({
  head: () => ({
    meta: [
      {
        title: "首页",
      },
    ],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { data: currentUser } = queries.auth.getCurrentSession.useSuspenseQuery(
    {
      select: (data) => {
        if (data.session) {
          return data.user;
        }
        throw new Error("Session not found");
      },
    },
  );

  const medals = Array.from({ length: 10 }).map((_, index) => ({
    id: index.toString(),
    name: "勋章",
    description: "这是一个勋章",
    iconUrl: `https://api.dicebear.com/9.x/bottts/svg?seed=Medal-${index}`,
    order: index,
  }));

  return (
    <div className="space-y-6">
      <section>
        <Profile user={currentUser} />
      </section>

      <Separator />

      <section>
        <h2 className="text-lg">每日任务</h2>
        <div className="mt-3">
          <DailyTasks />
        </div>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg">我的勋章</h2>
        <div className="mt-3">
          <MyMedals medals={medals} />
        </div>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg">账号关联</h2>
        <div className="mt-3">
          <ul className="divide-y">
            <li className="flex justify-between py-6 first:pt-3 last:pb-0">
              <div className="flex items-center">
                <div className="flex items-center gap-2 w-36">
                  <div
                    className="flex items-center justify-center rounded-full size-8"
                    style={{ backgroundColor: SiQqHex }}
                  >
                    <SiQq className="size-4" />
                  </div>
                  <span>QQ</span>
                  <span>：</span>
                </div>
                <span className="text-gray-fg-secondary">YueHaxgu月</span>
              </div>

              <Button intent="outline" flavor="gray">
                解除绑定
              </Button>
            </li>

            <li className="flex justify-between py-6 first:pt-0 last:pb-0">
              <div className="flex items-center">
                <div className="flex items-center gap-2 w-36">
                  <div
                    className="flex items-center justify-center rounded-full size-8"
                    style={{ backgroundColor: SiWechatHex }}
                  >
                    <SiWechat className="size-4" />
                  </div>
                  <span>微信</span>
                  <span>：</span>
                </div>
                <span className="text-gray-fg-secondary">未关联</span>
              </div>

              <Button intent="soft" flavor="accent">
                去关联
              </Button>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}

function Profile({ user }: { user: User }) {
  return (
    <div className="flex justify-between gap-16">
      <div className="flex gap-4 grow">
        <Image
          alt=""
          src={user.avatarUrl}
          className="size-30 bg-gray-solid"
          width={120}
          aspectRatio={1}
        />

        <div className="self-end w-full space-y-3">
          <div className="flex items-baseline gap-2">
            <h2 className="text-2xl font-bold">{user.nickname}</h2>
            <span className="text-xs text-gray-fg-secondary">社区等级</span>
            <Badge variant="default" size="xs">
              Lv. 3
            </Badge>
          </div>

          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-bold">Lv. 3</span>
              <span className="text-xs text-gray-fg-secondary">
                距离升级还需要 {Number.MIN_SAFE_INTEGER} EXP
              </span>
              <div className="ml-auto space-x-1 text-xs text-gray-fg-secondary">
                <span className="text-gray-fg-primary">1500</span>
                <span>/</span>
                <span>2500</span>
              </div>
            </div>

            <Progress className="h-6" value={33} />
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-between">
        <div className="flex gap-2">
          <Button intent="soft" flavor="gray" asChild>
            <Link to="/user/$username" params={{ username: user.userId }}>
              个人主页
            </Link>
          </Button>
          <Button intent="soft" flavor="gray" asChild>
            <Link to="/user/settings/profile">修改个人资料</Link>
          </Button>
        </div>

        <MedalDisplayCase
          className="self-end grid-cols-3 gap-3 2xl:grid-cols-3"
          medalSizeClassName="size-10"
          medals={Array.from({ length: 3 }).map((_, index) => ({
            id: index.toString(),
            name: "勋章",
            description: "这是一个勋章",
            iconUrl: `https://api.dicebear.com/9.x/bottts/svg?seed=Medal-${index}`,
            order: index,
          }))}
        />
      </div>
    </div>
  );
}

function DailyTasks() {
  return (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      <div className="flex border-2 border-accenta-solid">
        <div className="flex flex-col items-center justify-center size-20 bg-accenta-solid">
          <CheckIcon className="size-8" strokeWidth={5} />
        </div>
        <div className="flex flex-col justify-center gap-1 px-4 text-gray-fg-secondary">
          <dt className="text-gray-fg-primary">每日点赞</dt>
          <p className="text-xs not-sr-only">每日登录领取经验~</p>
          <span className="text-xs not-sr-only">已完成，获得 50 EXP</span>
          <dd className="sr-only">每日登录领取经验，当前已完成，获得 50 EXP</dd>
        </div>
      </div>

      <div className="flex border-2 border-accenta-ui">
        <div className="flex flex-col items-center justify-center size-20 bg-accenta-ui text-gray-fg-secondary">
          <span className="text-3xl font-bold">20</span>
          <span className="text-xs">EXP</span>
        </div>
        <div className="flex flex-col justify-center gap-1 px-4 text-gray-fg-secondary">
          <dt className="text-gray-fg-primary">每日点赞</dt>
          <p className="text-xs not-sr-only">每日登录领取经验~</p>
          <span className="text-xs not-sr-only">未完成</span>
          <dd className="sr-only">
            每日登录领取经验，当前未完成，完成可获得 20 EXP
          </dd>
        </div>
      </div>
    </dl>
  );
}

function MyMedals({ medals }: { medals: Medal[] }) {
  const [items, setItems] = useState(medals);

  function SortableMedalItem({ medal }: { medal: Medal }) {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: medal.id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };
    return (
      <div
        className="flex flex-col items-center gap-1 py-2 text-center bg-gray-ui"
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
      >
        <img alt="" src={medal.iconUrl} className="size-12" />
        <GripHorizontalIcon className="size-4 text-gray-fg-secondary" />
      </div>
    );
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) {
      return;
    }
    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-6 gap-4 2xl:grid-cols-7">
          {items.map((medal) => (
            <SortableMedalItem key={medal.id} medal={medal} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
